import React, { ReactNode, useEffect, useState } from 'react'

import { Box } from '../Box/Box'
import { Inline } from '../Inline/Inline'
import { Icon } from '../Icon/Icon'
import { Text } from '../Text/Text'
import { createPortal } from 'react-dom'

interface ToastPortalProps {
  children: ReactNode
}

const ToastPortal = ({ children }: ToastPortalProps) => {
  const [toastElement, setElement] = useState<HTMLElement | null>(null)

  useEffect(() => {
    const toastContainerId = 'pulsar-toast-container'
    let element = document.getElementById(toastContainerId)

    if (!element) {
      element = document.createElement('div')
      element.setAttribute('id', toastContainerId)
      element.setAttribute('class', '')

      document.body.appendChild(element)
    }

    setElement(element)
  }, [])

  if (!toastElement) {
    return null
  }

  return createPortal(<>{children}</>, toastElement)
}

type Type = 'info' | 'success' | 'caution' | 'critical'
export interface ToastProps {
  show: boolean
  type: Type
  message: string
  onClose: () => {}
}

type ToneMap = {
  [key in Type]: 'action' | 'positive' | 'caution' | 'negative'
}

export const Toast: React.FC<ToastProps> = ({ show, type = 'info', message, onClose }) => {
  const toneMap: ToneMap = {
    info: 'action',
    success: 'positive',
    caution: 'caution',
    critical: 'negative'
  }
  const [showToast, setShowToast] = useState(false)

  const [opacity, setOpacity] = useState(0)
  const [transform, setTransform] = useState(100)

  const openToast = () => {
    setShowToast(true)
    setTimeout(() => {
      setTransform(0)
      setOpacity(1)
    }, 80)
  }

  const closeToast = () => {
    setTransform(100)
    setOpacity(0)
    setTimeout(() => setShowToast(false), 80)
  }

  useEffect(() => {
    if (show) openToast()
    else closeToast()
  }, [show])

  return (
    <ToastPortal>
      {showToast && (
        <Box
          position="fixed"
          width="100%"
          inset="s"
          left={0}
          bottom={0}
          display="flex"
          justifyContent="center"
          opacity={opacity}
          transform={`translateY(${transform}%)`}
          transition="all .15s ease-in"
          zIndex="var(--zindex-toast)"
        >
          <Box
            border={`1px solid var(--colors-${toneMap[type]})`}
            radius="xxs"
            background={`var(--colors-${toneMap[type]}--subtle)`}
            insetStretch="s"
            zIndex="var(--zindex-toast)"
            maxWidth={800}
            width="100%"
          >
            <Inline space="xs" alignX="between" alignY="center" nowrap>
              <Icon name={type} size="m" />
              <Text component="p" tone={toneMap[type]} size={['small', 'base']} weight="strong">
                {message}
              </Text>
              <Box component="button" onClick={onClose} cursor="pointer">
                <Icon name="close" size="m" tone={toneMap[type]} />
              </Box>
            </Inline>
          </Box>
        </Box>
      )}
    </ToastPortal>
  )
}
